import React, { useState, useEffect, useMemo } from "react";
import Table from "./getCarTabel";
import { VEHICLE_LIST } from "../constant";
import { GET } from "../constant/apiMethods";
import { connect } from "react-redux";
import AddWheel from "../components/AddWheel/AddWheel";
import Labels from "../services/apiLabels";
import { getAuth, getAuthUserId } from "../services/localStorage";
import { FiSearch, FiX } from "react-icons/fi";
import { PaginationControl } from "react-bootstrap-pagination-control";
import config from "../config";
import { pickEvaluationsRaw } from "../../utils/accountActivity";
import { getBuyNowDisplayPrice } from "../utils/listingPrice";

const PAGE_SIZE_OPTIONS = [5, 10, 20];

function listingType(item) {
  const hasAuction = Array.isArray(item?.auction) && item.auction.length > 0;
  if (hasAuction) return "auction";
  if (item?.buy_now) return "buynow";
  return "unlisted";
}

function listingSortPrice(item) {
  return Number(getBuyNowDisplayPrice(item)) || 0;
}

function toTime(value) {
  if (value == null || value === "") return 0;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? 0 : t;
}

function toNumber(value) {
  const n = Number(value);
  return Number.isNaN(n) ? 0 : n;
}

function matchesSearch(item, query) {
  if (!query) return true;
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    item?.name,
    item?.brand_name,
    item?.model_name,
    item?.fuel_type,
    item?.registration_number,
    item?.year,
  ]
    .filter((v) => v != null && v !== "")
    .join(" ")
    .toLowerCase();
  return haystack.includes(q);
}

function valueLabel(value, ...keys) {
  if (value == null) return "";
  if (typeof value !== "object") return String(value);
  for (let i = 0; i < keys.length; i += 1) {
    if (value[keys[i]] != null && value[keys[i]] !== "") {
      return String(value[keys[i]]);
    }
  }
  return "";
}

function evaluationToVehicle(item, index) {
  const variant =
    valueLabel(item.variant_name, "display", "title", "name") ||
    valueLabel(item.car, "display", "title", "name");
  const brand = valueLabel(item.brand_name, "brand_name", "name", "display");
  const model = valueLabel(item.model_name, "display", "name", "title");
  const fuel = valueLabel(item.fuel_type, "display", "name", "title");
  const year = valueLabel(item.year_name || item.year, "display", "title", "name");
  const registration = String(item.reg_number || item.registration_number || "");

  return {
    ...item,
    id: item.id || item.pk || item.evaluation_id || `evaluation-${index}`,
    name:
      item.name ||
      [brand, model, variant].filter(Boolean).join(" ") ||
      registration ||
      "Vehicle evaluation",
    brand_name: brand,
    model_name: model || variant,
    fuel_type: fuel,
    year,
    registration_number: registration,
    date_added:
      item.created_at || item.created || item.date || item.last_evaluation || "",
    _evaluationOnly: true,
  };
}

function vehicleIdentity(item) {
  const registration = String(
    item?.registration_number || item?.reg_number || ""
  )
    .replace(/\s+/g, "")
    .toUpperCase();
  if (registration) return `reg:${registration}`;
  return "";
}

const Tabs = ({
  getCarWiners,
  myVehiclesList,
  user_id,
  loading,
  latestEvaluation,
}) => {
  const [selectedTabIdx, setSelectedTabIdx] = useState(0);
  const [isEdit, setisEdit] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [listingFilter, setListingFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [evaluationVehicles, setEvaluationVehicles] = useState([]);
  const [evaluationLoading, setEvaluationLoading] = useState(false);
  const [listingPrefill, setListingPrefill] = useState(null);

  const tabs = [
    { name: "All vehicles" },
    { name: isEdit ? "Update vehicle" : "Add vehicle" },
  ];

  const productList = Array.isArray(myVehiclesList) ? myVehiclesList : [];
  const latestEvaluationVehicle =
    latestEvaluation?.id || latestEvaluation?.tracking_number
      ? evaluationToVehicle(latestEvaluation, -1)
      : null;
  const allEvaluations = latestEvaluationVehicle
    ? [
        latestEvaluationVehicle,
        ...evaluationVehicles.filter(
          (item) => String(item.id) !== String(latestEvaluationVehicle.id)
        ),
      ]
    : evaluationVehicles;
  const productKeys = new Set(productList.map(vehicleIdentity).filter(Boolean));
  const sourceList = [
    ...productList,
    ...allEvaluations.filter((item) => {
      const key = vehicleIdentity(item);
      return !key || !productKeys.has(key);
    }),
  ];
  const isLoading =
    (Boolean(loading) || evaluationLoading) && sourceList.length === 0;

  useEffect(() => {
    getAllCars();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, listingFilter, sortBy, pageSize]);

  const getAllCars = () => {
    const id = user_id || getAuthUserId();
    if (!id) return;
    getCarWiners(id);
    loadDealerEvaluations(id);
  };

  const loadDealerEvaluations = async (id) => {
    const token = getAuth()?.token;
    if (!token) {
      setEvaluationVehicles([]);
      return;
    }
    setEvaluationLoading(true);
    try {
      const query = new URLSearchParams({ user_id: String(id) });
      const response = await fetch(
        `${config.baseUrl}${Labels.user_evaluations}?${query.toString()}`,
        {
          headers: {
            Accept: "application/json",
            Authorization: `Token ${token}`,
          },
        }
      );
      if (!response.ok) throw new Error("Unable to load evaluations");
      const json = await response.json();
      setEvaluationVehicles(
        pickEvaluationsRaw(json).map(evaluationToVehicle).filter(Boolean)
      );
    } catch (error) {
      setEvaluationVehicles([]);
    } finally {
      setEvaluationLoading(false);
    }
  };

  const filteredList = useMemo(() => {
    let rows = [...sourceList];

    rows = rows.filter((item) => matchesSearch(item, search));

    if (statusFilter === "active") {
      rows = rows.filter((item) => Boolean(item?.status));
    } else if (statusFilter === "inactive") {
      rows = rows.filter((item) => !item?.status);
    }

    if (listingFilter !== "all") {
      rows = rows.filter((item) => listingType(item) === listingFilter);
    }

    rows.sort((a, b) => {
      if (sortBy === "newest") {
        return (
          toTime(b?.date_added || b?.creationdate) -
          toTime(a?.date_added || a?.creationdate)
        );
      }
      if (sortBy === "oldest") {
        return (
          toTime(a?.date_added || a?.creationdate) -
          toTime(b?.date_added || b?.creationdate)
        );
      }
      if (sortBy === "name_asc") {
        return String(a?.name || "").localeCompare(String(b?.name || ""));
      }
      if (sortBy === "price_desc") {
        return listingSortPrice(b) - listingSortPrice(a);
      }
      if (sortBy === "price_asc") {
        return listingSortPrice(a) - listingSortPrice(b);
      }
      return 0;
    });

    return rows;
  }, [sourceList, search, statusFilter, listingFilter, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filteredList.length / pageSize) || 1);
  const safePage = Math.min(page, totalPages);
  const pageStart = filteredList.length ? (safePage - 1) * pageSize : 0;
  const pageEnd = Math.min(pageStart + pageSize, filteredList.length);
  const pagedList = filteredList.slice(pageStart, pageEnd);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  // Evaluations are not products, so they cannot be auctioned directly. Send the
  // dealer to the add-vehicle form seeded with the evaluated car; saving there
  // creates the product the auction/buy-now endpoints need.
  const startListingFromEvaluation = (item) => {
    setisEdit(null);
    setListingPrefill({
      registration_number: String(
        item?.registration_number || item?.reg_number || ""
      ).toUpperCase(),
      name: item?.name || "",
      millage: item?.millage ?? item?.mileage ?? item?.Mileage ?? "",
      postcode: item?.postcode || item?.Postcode || "",
    });
    setSelectedTabIdx(1);
  };

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setListingFilter("all");
    setSortBy("newest");
    setPage(1);
  };

  const hasActiveFilters =
    Boolean(search.trim()) ||
    statusFilter !== "all" ||
    listingFilter !== "all" ||
    sortBy !== "newest";

  return (
    <div className="dd-section">
      <nav className="dd-tabs" aria-label="Vehicle sections">
        {tabs.map((tab, index) => (
          <button
            key={tab.name}
            type="button"
            className={`dd-tab${index === selectedTabIdx ? " is-active" : ""}`}
            onClick={() => {
              setSelectedTabIdx(index);
              setisEdit(null);
              setListingPrefill(null);
            }}
          >
            {tab.name}
            {index === 0 ? (
              <span className="dd-tab__count">{sourceList.length || 0}</span>
            ) : null}
          </button>
        ))}
      </nav>

      {selectedTabIdx === 0 &&
        (isLoading ? (
          <div className="dd-empty" aria-busy="true">
            <strong>Loading vehicles…</strong>
            <p>Fetching your listings from the dealer desk.</p>
          </div>
        ) : sourceList.length ? (
          <>
            <div className="dd-toolbar">
              <label className="dd-toolbar__search">
                <FiSearch size={16} aria-hidden />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name, brand, reg…"
                  aria-label="Search vehicles"
                />
                {search ? (
                  <button
                    type="button"
                    className="dd-toolbar__clear-input"
                    onClick={() => setSearch("")}
                    aria-label="Clear search"
                  >
                    <FiX size={14} />
                  </button>
                ) : null}
              </label>

              <label className="dd-toolbar__field">
                <span>Status</span>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </label>

              <label className="dd-toolbar__field">
                <span>Listing</span>
                <select
                  value={listingFilter}
                  onChange={(e) => setListingFilter(e.target.value)}
                >
                  <option value="all">All</option>
                  <option value="auction">Auction</option>
                  <option value="buynow">Buy now</option>
                  <option value="unlisted">Unlisted</option>
                </select>
              </label>

              <label className="dd-toolbar__field">
                <span>Sort</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="newest">Newest</option>
                  <option value="oldest">Oldest</option>
                  <option value="name_asc">Name · A–Z</option>
                  <option value="price_desc">Price · high to low</option>
                  <option value="price_asc">Price · low to high</option>
                </select>
              </label>

              <label className="dd-toolbar__field">
                <span>Per page</span>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>

              {hasActiveFilters ? (
                <button
                  type="button"
                  className="dd-btn dd-btn--ghost"
                  onClick={clearFilters}
                >
                  Reset
                </button>
              ) : null}
            </div>

            <div className="dd-toolbar__meta">
              {filteredList.length ? (
                <span>
                  Showing {pageStart + 1}–{pageEnd} of {filteredList.length}
                  {filteredList.length !== sourceList.length
                    ? ` (filtered from ${sourceList.length})`
                    : ""}
                </span>
              ) : (
                <span>No matching vehicles</span>
              )}
            </div>

            {filteredList.length ? (
              <Table
                setSelectedTabIdx={setSelectedTabIdx}
                setisEdit={setisEdit}
                getAllCars={getAllCars}
                myVehiclesList={pagedList}
                onCreateListing={startListingFromEvaluation}
              />
            ) : (
              <div className="dd-empty">
                <strong>No matches</strong>
                <p>Try clearing filters or changing your search.</p>
                <button
                  type="button"
                  className="dd-btn dd-btn--ghost"
                  onClick={clearFilters}
                >
                  Reset filters
                </button>
              </div>
            )}

            {filteredList.length > pageSize ? (
              <div className="dd-pagination">
                <PaginationControl
                  page={safePage}
                  between={3}
                  total={filteredList.length}
                  limit={pageSize}
                  changePage={(next) => setPage(next)}
                  ellipsis={1}
                />
              </div>
            ) : null}
          </>
        ) : (
          <div className="dd-empty">
            <strong>No vehicles yet</strong>
            <p>Add a vehicle to list it for auction or buy now.</p>
            <button
              type="button"
              className="dd-btn dd-btn--primary"
              onClick={() => setSelectedTabIdx(1)}
            >
              Add vehicle
            </button>
          </div>
        ))}

      {selectedTabIdx === 1 && (
        <div className="dd-form">
          <AddWheel
            setisEdit={setisEdit}
            isEdit={isEdit}
            setSelectedTabIdx={setSelectedTabIdx}
            getAllCars={getAllCars}
            prefill={listingPrefill}
            onPrefillConsumed={() => setListingPrefill(null)}
          />
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  user_id: state?.auth?.id,
  myVehiclesList: state?.myVehicles?.data,
  loading: state?.myVehicles?.loading,
  latestEvaluation: state?.sellMyCar?.evaluation_create,
});

const mapDispatchToProps = (dispatch) => ({
  getCarWiners: (id) =>
    dispatch({
      type: VEHICLE_LIST,
      payload: {
        type: VEHICLE_LIST,
        label: `${Labels.vechiellist}${id}/`,
        method: GET,
        hideOverlayLoader: true,
        hideSuccessToaster: true,
      },
    }),
});

export default connect(mapStateToProps, mapDispatchToProps)(Tabs);
